import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useContent } from '../context/ContentContext';
import { useTranslation } from 'react-i18next';

const SiteSettings = () => {
  const { content, updateContent } = useContent();
  const [editedContent, setEditedContent] = useState(content);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { t } = useTranslation();

  const handleChange = (section, field, value) => {
    const newContent = { ...editedContent };
    newContent[section][field] = value;
    setEditedContent(newContent);
  };

  // 处理双语字段变更
  const handleBilingualChange = (section, field, lang, value) => {
    const newContent = { ...editedContent };
    const fieldName = lang === 'zh' ? `${field}_zh` : field;
    newContent[section][fieldName] = value;
    setEditedContent(newContent);
  };

  const handleSocialChange = (network, value) => {
    const newContent = { ...editedContent };
    newContent.site.social[network] = value;
    setEditedContent(newContent);
  };

  // 导航管理函数
  const handleNavigationChange = (index, field, value) => {
    const newContent = { ...editedContent };
    newContent.header.navigation[index][field] = value;
    setEditedContent(newContent);
  };

  const handleAddNavItem = () => {
    const newContent = { ...editedContent };
    newContent.header.navigation.push({
      title: 'New Link',
      title_zh: '新链接',
      path: '/'
    });
    setEditedContent(newContent);
  };

  const handleDeleteNavItem = (index) => {
    const newContent = { ...editedContent };
    newContent.header.navigation.splice(index, 1);
    setEditedContent(newContent);
  };

  const handleMoveNavItem = (index, direction) => {
    const newContent = { ...editedContent };
    const nav = newContent.header.navigation;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < nav.length) {
      [nav[index], nav[newIndex]] = [nav[newIndex], nav[index]];
      setEditedContent(newContent);
    }
  };

  const handleSaveChanges = () => {
    updateContent(editedContent);
    setOpenSnackbar(true);
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Site Settings
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />}
          onClick={handleSaveChanges}
        >
          {t('common.saveChanges')}
        </Button>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              General Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Title (English)"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.title}
                  onChange={(e) => handleBilingualChange('site', 'title', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Title (中文)"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.title_zh || ''}
                  onChange={(e) => handleBilingualChange('site', 'title', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Description (English)"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.description}
                  onChange={(e) => handleBilingualChange('site', 'description', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Description (中文)"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.description_zh || ''}
                  onChange={(e) => handleBilingualChange('site', 'description', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Logo URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.logo}
                  onChange={(e) => handleChange('site', 'logo', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Favicon URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.favicon}
                  onChange={(e) => handleChange('site', 'favicon', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Contact Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Office Phone Number"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.phone}
                  onChange={(e) => handleChange('site', 'phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Cell Phone Number"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.cell || ''}
                  onChange={(e) => handleChange('site', 'cell', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Fax Number"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.fax || ''}
                  onChange={(e) => handleChange('site', 'fax', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email Address"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.email}
                  onChange={(e) => handleChange('site', 'email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Office Address (English)"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  value={editedContent.site.address}
                  onChange={(e) => handleBilingualChange('site', 'address', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Office Address (中文)"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  value={editedContent.site.address_zh || ''}
                  onChange={(e) => handleBilingualChange('site', 'address', 'zh', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Social Media
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Facebook URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.social.facebook}
                  onChange={(e) => handleSocialChange('facebook', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Twitter URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.social.twitter}
                  onChange={(e) => handleSocialChange('twitter', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Instagram URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.social.instagram}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="LinkedIn URL"
                  fullWidth
                  margin="normal"
                  value={editedContent.site.social.linkedin}
                  onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Header CTA
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="CTA Text (English)"
                  fullWidth
                  margin="normal"
                  value={editedContent.header.cta.text}
                  onChange={(e) => {
                    const newContent = { ...editedContent };
                    newContent.header.cta.text = e.target.value;
                    setEditedContent(newContent);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="CTA Text (中文)"
                  fullWidth
                  margin="normal"
                  value={editedContent.header.cta.text_zh || ''}
                  onChange={(e) => {
                    const newContent = { ...editedContent };
                    newContent.header.cta.text_zh = e.target.value;
                    setEditedContent(newContent);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="CTA URL Path"
                  fullWidth
                  margin="normal"
                  value={editedContent.header.cta.path}
                  onChange={(e) => {
                    const newContent = { ...editedContent };
                    newContent.header.cta.path = e.target.value;
                    setEditedContent(newContent);
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Navigation Management - Full Width */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Navigation Menu
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddNavItem}
                size="small"
              >
                Add Link
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title (EN)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title (中文)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedContent.header.navigation.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveNavItem(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveNavItem(index, 'down')}
                            disabled={index === editedContent.header.navigation.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.title}
                          onChange={(e) => handleNavigationChange(index, 'title', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.title_zh || ''}
                          onChange={(e) => handleNavigationChange(index, 'title_zh', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.path}
                          onChange={(e) => handleNavigationChange(index, 'path', e.target.value)}
                          fullWidth
                          placeholder="e.g., /services or /#about"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteNavItem(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              Use "/" for home, "/services" for separate pages, or "/#section" for anchor links to sections on the home page.
            </Alert>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled">
          Settings updated successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteSettings; 